import { Permission, RequestType } from '@i9portal/shared';
import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { AppLayout } from '../components/layout/app-layout';
import { PublicLayout } from '../components/layout/public-layout';
import { ForgotPasswordPage } from '../features/auth/forgot-password-page';
import { LoginPage } from '../features/auth/login-page';
import { ResetPasswordPage } from '../features/auth/reset-password-page';
import { HomePage } from '../features/home/home-page';
import { RequireAuth, RequireEmployee, RequirePermission } from '../lib/auth/guards';
import { NotFoundPage } from './not-found-page';

/** React.lazy para módulos com exportação nomeada (divide o bundle por área). */
function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
) {
  return lazy(async () => ({
    default: (await loader())[name] as ComponentType<Record<string, never>>,
  }));
}

// Funcionário
const OvertimeListPage = lazyNamed(
  () => import('../features/overtime/overtime-list-page'),
  'OvertimeListPage',
);
const OvertimeFormPageLazy = lazy(async () => ({
  default: (await import('../features/overtime/overtime-form-page')).OvertimeFormPage,
}));
const ReimbursementListPage = lazyNamed(
  () => import('../features/reimbursements/reimbursement-list-page'),
  'ReimbursementListPage',
);
const ReimbursementFormPageLazy = lazy(async () => ({
  default: (await import('../features/reimbursements/reimbursement-form-page'))
    .ReimbursementFormPage,
}));
const RequestDetailPageLazy = lazy(async () => ({
  default: (await import('../features/requests/request-detail-page')).RequestDetailPage,
}));
const MySchedulePage = lazyNamed(
  () => import('../features/schedule/my-schedule-page'),
  'MySchedulePage',
);
const NotificationsPage = lazyNamed(
  () => import('../features/notifications/notifications-page'),
  'NotificationsPage',
);
const ProfilePage = lazyNamed(() => import('../features/profile/profile-page'), 'ProfilePage');

// RH
const RhDashboardPage = lazyNamed(
  () => import('../features/rh/rh-dashboard-page'),
  'RhDashboardPage',
);
const RhRequestsPage = lazyNamed(() => import('../features/rh/rh-requests-page'), 'RhRequestsPage');
const RhEmployeesPage = lazyNamed(
  () => import('../features/rh/rh-employees-page'),
  'RhEmployeesPage',
);
const RhPaymentsPage = lazyNamed(() => import('../features/rh/rh-payments-page'), 'RhPaymentsPage');

// Administração
const UsersPage = lazyNamed(() => import('../features/admin/users-page'), 'UsersPage');
const UserDetailPage = lazyNamed(
  () => import('../features/admin/user-detail-page'),
  'UserDetailPage',
);
const HolidaysPage = lazyNamed(() => import('../features/admin/holidays-page'), 'HolidaysPage');
const CatalogPage = lazyNamed(() => import('../features/admin/catalog-page'), 'CatalogPage');
const SettingsPage = lazyNamed(() => import('../features/admin/settings-page'), 'SettingsPage');
const AuditPage = lazyNamed(() => import('../features/admin/audit-page'), 'AuditPage');
const OutboxPage = lazyNamed(() => import('../features/admin/outbox-page'), 'OutboxPage');

const P = Permission;

export const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/esqueci-senha', element: <ForgotPasswordPage /> },
      { path: '/redefinir-senha', element: <ResetPasswordPage mode="reset" /> },
      { path: '/ativar-conta', element: <ResetPasswordPage mode="activate" /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <HomePage /> },
          {
            element: <RequireEmployee />,
            children: [
              { path: '/horas-extras', element: <OvertimeListPage /> },
              { path: '/horas-extras/nova', element: <OvertimeFormPageLazy mode="create" /> },
              {
                path: '/horas-extras/:id',
                element: <RequestDetailPageLazy type={RequestType.OVERTIME} variant="employee" />,
              },
              { path: '/horas-extras/:id/editar', element: <OvertimeFormPageLazy mode="edit" /> },
              { path: '/reembolsos', element: <ReimbursementListPage /> },
              { path: '/reembolsos/novo', element: <ReimbursementFormPageLazy mode="create" /> },
              {
                path: '/reembolsos/:id',
                element: (
                  <RequestDetailPageLazy type={RequestType.REIMBURSEMENT} variant="employee" />
                ),
              },
              {
                path: '/reembolsos/:id/editar',
                element: <ReimbursementFormPageLazy mode="edit" />,
              },
              { path: '/minha-jornada', element: <MySchedulePage /> },
            ],
          },
          { path: '/notificacoes', element: <NotificationsPage /> },
          { path: '/perfil', element: <ProfilePage /> },

          {
            path: '/rh',
            element: <RequirePermission permission={P.DASHBOARD_READ} />,
            children: [
              { index: true, element: <RhDashboardPage /> },
              { path: 'funcionarios', element: <RhEmployeesPage /> },
            ],
          },
          {
            path: '/rh',
            element: <RequirePermission permission={P.REQUESTS_READ_ALL} />,
            children: [
              { path: 'solicitacoes', element: <RhRequestsPage /> },
              { path: 'solicitacoes/:type/:id', element: <RequestDetailPageLazy variant="hr" /> },
              { path: 'pagamentos', element: <RhPaymentsPage /> },
            ],
          },

          {
            path: '/admin',
            children: [
              { index: true, element: <Navigate to="/admin/usuarios" replace /> },
              {
                element: <RequirePermission permission={P.USERS_READ} />,
                children: [
                  { path: 'usuarios', element: <UsersPage /> },
                  { path: 'usuarios/:id', element: <UserDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={P.CATALOG_MANAGE} />,
                children: [
                  { path: 'feriados', element: <HolidaysPage /> },
                  { path: 'cadastros', element: <CatalogPage /> },
                ],
              },
              {
                element: <RequirePermission permission={P.SETTINGS_MANAGE} />,
                children: [{ path: 'configuracoes', element: <SettingsPage /> }],
              },
              {
                element: <RequirePermission permission={P.AUDIT_READ} />,
                children: [{ path: 'auditoria', element: <AuditPage /> }],
              },
              {
                element: <RequirePermission permission={P.NOTIFICATIONS_MANAGE} />,
                children: [{ path: 'notificacoes', element: <OutboxPage /> }],
              },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
